import React, { useState } from 'react'

import { getCartProgress } from 'Utils/getCartProgress'

let CartWidget

// Lazy-loads the cart popover widget on first interaction, builds the popover
// node, and carries the GA4 remove-from-cart tracking the widget calls back into.
function useCartWidget({ cart, fetchNextCartDataPage, locale, showCartAds, isCurrentUser }) {
  const [isWidgetLoaded, setIsWidgetLoaded] = useState(false)

  function loadWidget(callback) {
    if (CartWidget) {
      callback?.()
      return
    }

    import('Pods/LazyLoaded/CartWidget/CartWidget')
      .then((module) => {
        CartWidget = module.default
        setIsWidgetLoaded(true)
        callback?.()
      })
      .catch((error) => {
        console.error('Failed to load CartWidget:', error)
      })
  }

  function prepareTrackViewItemList(attributes) {
    const searchQuery = attributes.searchQuery || ''

    return {
      item_id: String(attributes.id),
      item_name: attributes.title,
      affiliation: attributes.affiliation || '',
      currency: 'USD',
      index: attributes.index,
      item_brand: attributes.authorUsername,
      item_category: attributes.category,
      item_category2: attributes.subcategory,
      item_category3: attributes.itemCategory3,
      item_category4: attributes.itemCategory4,
      item_category5: attributes.itemCategory5,
      item_list_id: searchQuery,
      item_list_name: searchQuery,
      item_variant: attributes.productType,
      location_id: attributes.locationId && attributes.locationId.join('_'),
      price: parseFloat(attributes.price),
      quantity: 1,
      discount: attributes.discount > 0 ? parseFloat(attributes.discount) : 0,
    }
  }

  function getUserId() {
    if (typeof window !== 'undefined') {
      // take user id from APP.config.userId - for slow application
      // or from window.pageConfig.currentUserId - for fast application
      return window.App?.config?.userId || window.pageConfig?.currentUserId
    }

    return ''
  }

  function trackCartRemoveGA4(itemId) {
    const items = cart.data
    const { meta } = cart
    const currentItem = items.find((item) => Number(item.id) === itemId)
    const currentTrackingItem = meta.trackingCartItems.find(
      (item) => item.id === Number(currentItem.relationships.item.data.id)
    )

    if (window.dataLayer && meta) {
      const temp = prepareTrackViewItemList({
        ...currentTrackingItem,
        price: currentItem.attributes.price,
        discount: currentItem.attributes.discount,
      })

      window.dataLayer.push({
        event: 'remove_from_cart',
        user_id: getUserId(),
        ecommerce: {
          currency: 'USD',
          value: currentItem.attributes.price,
          items: [temp],
        },
        _clear: true,
      })
    }
  }

  const shouldShowCartProgress = !!cart.meta.saleThreshold
  const cartProgress = getCartProgress(cart.meta.totalPrice, cart.meta.saleThreshold)
  const isLoaded = isWidgetLoaded && !!CartWidget

  const popoverComponent = isLoaded ? (
    <CartWidget
      items={cart.data}
      meta={cart.meta}
      included={cart.included}
      onLoadNextPage={fetchNextCartDataPage}
      isLastPageLoaded={cart.isLastPageLoaded}
      locale={locale}
      trackCartRemoveGA4={trackCartRemoveGA4}
      shouldShowCartProgress={shouldShowCartProgress}
      showCartAds={showCartAds}
      isCurrentUser={isCurrentUser}
      onCloseCart={() => App.events.publish('top:menu:cart:close')}
    />
  ) : null

  return {
    isLoaded,
    loadWidget,
    popoverComponent,
    shouldShowCartProgress,
    cartProgress,
  }
}

export { useCartWidget }
